import { useState } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  Legend,
} from "recharts";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";

// Generate data points for the emissions pathway
const generateData = () => {
  const data = [];
  const baseYear = 2026;
  const endYear = 2050;
  const baseEmissions = 55;

  for (let year = baseYear; year <= endYear; year++) {
    const yearIndex = year - baseYear;
    
    // Business as Usual - increases over time
    const businessAsUsual = Math.round(baseEmissions + (yearIndex * 2.5));
    
    // Target Line - decreases to near zero by 2050
    const targetLine = Math.round(baseEmissions * Math.pow(0.92, yearIndex));
    
    // Planned Line - between BAU and target
    const plannedLine = Math.round(baseEmissions * Math.pow(0.96, yearIndex) + 5);

    data.push({
      year,
      businessAsUsual,
      plannedLine,
      targetLine,
      // Only show actual emissions for past/current years
      actualEmissions: year <= 2030 ? (year === 2026 ? 55 : year === 2030 ? 38 : null) : null,
    });
  }
  return data;
};

const data = generateData();

interface CustomTooltipProps {
  active?: boolean;
  payload?: Array<{
    name: string;
    value: number;
    color: string;
  }>;
  label?: number;
}

const CustomTooltip = ({ active, payload, label }: CustomTooltipProps) => {
  if (active && payload && payload.length) {
    // Filter out null values
    const validPayload = payload.filter(entry => entry.value !== null && entry.value !== undefined);
    
    if (validPayload.length === 0) return null;
    
    return (
      <div className="bg-white dark:bg-gray-900 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 p-4 min-w-[200px] z-50 relative">
        <p className="font-semibold text-gray-900 dark:text-white mb-2">Year: {label}</p>
        {validPayload.map((entry, index) => {
          const displayName = 
            entry.name === "businessAsUsual" ? "Business as Usual" :
            entry.name === "plannedLine" ? "Planned Line" :
            entry.name === "targetLine" ? "Target Line" :
            "Actual Emissions";
          
          const colorClass = 
            entry.name === "businessAsUsual" ? "text-red-500" :
            entry.name === "plannedLine" ? "text-orange-500" :
            entry.name === "targetLine" ? "text-green-500" :
            "text-blue-500";

          return (
            <p key={index} className={`${colorClass} text-sm font-medium`}>
              {displayName}: {entry.value} tCO2e
            </p>
          );
        })}
      </div>
    );
  }
  return null;
};

const SBTIEmissionsChart = () => {
  const [hoveredYear, setHoveredYear] = useState<number | null>(null);

  return (
    <div className="bg-white dark:bg-dark-foreground/5 rounded-xl p-4 sm:p-6 h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-foreground dark:text-dark-foreground">
          Emissions Pathway
        </h3>
        <Button 
          variant="outline" 
          size="sm" 
          className="text-xs border-border hover:bg-accent hover:text-accent-foreground"
        >
          <Plus className="w-3 h-3 mr-1" />
          Add Project
        </Button>
      </div>

      {/* Chart */}
      <div className="flex-1 w-full relative z-10 min-h-[260px]">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={data}
            margin={{ top: 10, right: 10, left: -10, bottom: 10 }}
            onMouseMove={(e) => {
              if (e.activeLabel) {
                setHoveredYear(Number(e.activeLabel));
              }
            }}
            onMouseLeave={() => setHoveredYear(null)}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.5} />
            <XAxis 
              dataKey="year" 
              tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 10 }}
              tickLine={{ stroke: 'hsl(var(--border))' }}
              axisLine={{ stroke: 'hsl(var(--border))' }}
              interval={2}
            />
            <YAxis 
              tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 10 }}
              tickLine={{ stroke: 'hsl(var(--border))' }}
              axisLine={{ stroke: 'hsl(var(--border))' }}
              label={{ 
                value: 'Emissions (tCO2e)', 
                angle: -90, 
                position: 'insideLeft',
                style: { fill: 'hsl(var(--muted-foreground))', fontSize: 10 }
              }}
            />
            <Tooltip content={<CustomTooltip />} cursor={{ strokeDasharray: '3 3' }} wrapperStyle={{ zIndex: 100 }} />
            
            {/* Near Term Target Reference Line */}
            <ReferenceLine 
              x={2030} 
              stroke="hsl(var(--primary))" 
              strokeDasharray="5 5"
              label={{ 
                value: "Near Term Target", 
                position: "top",
                fill: "hsl(var(--primary))",
                fontSize: 9
              }}
            />

            {/* Business as Usual Line */}
            <Line
              type="monotone"
              dataKey="businessAsUsual"
              stroke="#ef4444"
              strokeWidth={2}
              strokeDasharray="8 4"
              dot={false}
              activeDot={{ r: 6, fill: "#ef4444" }}
            />

            {/* Planned Line */}
            <Line
              type="monotone"
              dataKey="plannedLine"
              stroke="#f97316"
              strokeWidth={2}
              strokeDasharray="8 4"
              dot={false}
              activeDot={{ r: 6, fill: "#f97316" }}
            />

            {/* Target Line */}
            <Line
              type="monotone"
              dataKey="targetLine"
              stroke="hsl(var(--accent))"
              strokeWidth={2}
              strokeDasharray="8 4"
              dot={false}
              activeDot={{ r: 6, fill: "hsl(142, 76%, 36%)" }}
            />

            {/* Actual Emissions */}
            <Line
              type="monotone"
              dataKey="actualEmissions"
              stroke="#3b82f6"
              strokeWidth={0}
              dot={{ r: 5, fill: "#3b82f6" }}
              activeDot={{ r: 7, fill: "#3b82f6" }}
              connectNulls={false}
            />

            <Legend 
              wrapperStyle={{ fontSize: '10px', paddingTop: '10px' }}
              formatter={(value) => {
                const labels: Record<string, string> = {
                  businessAsUsual: "Business as Usual",
                  plannedLine: "Planned Line (with Carbon Reduction)",
                  targetLine: "Target Line",
                  actualEmissions: "Actual Emissions"
                };
                return <span className="text-muted-foreground">{labels[value] || value}</span>;
              }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default SBTIEmissionsChart;
